import * as THREE from 'three';
import socketService from './SocketService';
import Player, { ServerPlayerUpdate } from './entities/Player';
import ProjectileSystem from './ProjectileSystem';
import { GameEvents } from '../shared/constants';

type PlayerData = { id: string; name: string; color: string };
type PositionData = { x: number; y: number; z: number };
type RotationData = { x: number; y: number; z: number; w: number };

export default class Multiplayer {
  scene: THREE.Scene;
  collidables: THREE.Object3D[];
  players: Record<string, Player> = {};
  myUserId: string | null;
  projectileSystem: ProjectileSystem;
  selfTank: any | null;

  // Stable socket listener references
  private onRemovePlayerRef = (playerId: string) => this.removePlayer(playerId);
  private onConnectedPlayersRef = (data: { players: Record<string, PlayerData> | PlayerData[] }) => this.connectedPlayers(data);
  private onGameActionRef = (msg: { type: string; playerId: string; data: any }) => this.onGameAction(msg);

  constructor(
    scene: THREE.Scene,
    collidables: THREE.Object3D[],
    projectileSystem: ProjectileSystem,
    selfTank: any | null
  ) {
    this.scene = scene;
    this.collidables = collidables;
    this.myUserId = socketService.getSocketId();
    console.log(`Multiplayer initialized with myUserId: ${this.myUserId}`);
    this.projectileSystem = projectileSystem;
    this.selfTank = selfTank;
  }

  initializeListeners(): void {
    console.log('initializeListeners');
    if (!socketService.socket) {
      console.error('Socket not connected. Cannot initialize listeners.');
      return;
    }
    // Consolidated game:action listener
    socketService.socket.on(GameEvents.GAME_ACTION, this.onGameActionRef);
    socketService.socket.on(GameEvents.PLAYER_DISCONNECTED, this.onRemovePlayerRef);
    socketService.socket.on(GameEvents.CONNECTED_PLAYERS, this.onConnectedPlayersRef);
  }

  removeListeners(): void {
    console.log('removeListeners');
    if (!socketService.socket) {
      console.error('Socket not connected. Cannot remove listeners.');
      return;
    }
    // no-op: addPlayer event removed; handled via game:action player:spawn
    socketService.socket.off(GameEvents.GAME_ACTION, this.onGameActionRef);
    socketService.socket.off(GameEvents.PLAYER_DISCONNECTED, this.onRemovePlayerRef);
    socketService.socket.off(GameEvents.CONNECTED_PLAYERS, this.onConnectedPlayersRef);
  }

  onGameAction(msg: { type: string; playerId: string; data: any }) {
    if (!msg || !msg.type) return;
    const { type, playerId, data } = msg as any;
    switch (type) {
      case 'player:update': {
        if (playerId === this.myUserId) return;
        try {
          this.playerUpdate({ id: playerId, ...(data || {}) });
        } catch (e) {
          console.warn('Failed to apply player:update', e);
        }
        break;
      }
      case 'player:spawn': {
        if (playerId === this.myUserId) return;
        const info = {
          id: playerId,
          name: (data && data.name) || 'Player',
          color: (data && data.color) || 'T90A',
        };
        if (!this.players[playerId]) {
          try { this.addPlayer(info); } catch {}
        }
        break;
      }
      case 'projectile:fire': {
        // Ignore own echo; local fire already simulated
        if (playerId === this.myUserId) return;
        try { this.fireProjectile(data); } catch {}
        break;
      }
      case 'damage:deal': {
        try { this.dealDamage({ id: playerId, ...(data || {}) }); } catch {}
        break;
      }
      case 'player:dead': {
        try { this.playerDead({ id: playerId }); } catch {}
        break;
      }
      case 'player:respawn': {
        const info = {
          id: playerId,
          name: (data && data.name) || 'Player',
          color: (data && data.color) || 'T90A',
        };
        if (playerId === this.myUserId) {
          // Ignore self respawn announcement
          break;
        }
        if (!this.players[playerId]) {
          try { this.addPlayer(info); } catch {}
        } else {
          // Optional: update name/color on existing
          try {
            const p = this.players[playerId] as any;
            if (p && p.tankModel) {
              // no-op; adding could lead to duplicates
            }
          } catch {}
        }
        break;
      }
      default:
        // Unknown/unused action types are ignored
        break;
    }
  }

  requestPlayers(): void {
    if (!socketService.socket) {
      console.error('Socket not connected. Cannot request players.');
      return;
    }
    socketService.requestPlayers();
  }

  connectedPlayers(data: { players: Record<string, PlayerData> | PlayerData[] }): void {
    const { players } = data;
    console.log('connectedPlayers', JSON.stringify(data));
    const myId = socketService.getSocketId();

    if (Array.isArray(players)) {
      players.forEach((playerData) => {
        const playerId = playerData.id;
        if (playerId === myId) return;
        if (!this.players[playerId]) {
          console.log(`Adding existing player from array: ${playerData.name} (${playerId})`);
          const player = new Player(this.scene, playerData.name, playerId, playerData.color, this.collidables);
          if (player.tankModel) this.collidables.push(player.tankModel);
          this.players[playerId] = player;
        }
      });
    } else {
      for (const playerId in players) {
        const playerData = (players as Record<string, PlayerData>)[playerId];
        if (playerId === myId) continue;
        if (!this.players[playerId]) {
          console.log(`Adding existing player from object: ${playerData.name} (${playerId})`);
          const player = new Player(this.scene, playerData.name, playerId, playerData.color, this.collidables);
          if (player.tankModel) this.collidables.push(player.tankModel);
          this.players[playerId] = player;
        }
      }
    }

    console.log('Current players after connectedPlayers:', Object.keys(this.players));
  }

  fireProjectile(data: { turretPosition: PositionData; turretDirection: RotationData; weapon?: string }): void {
    const { turretPosition, turretDirection, weapon } = data;
    const tPos = new THREE.Vector3(turretPosition.x, turretPosition.y, turretPosition.z);
    const tDir = new THREE.Quaternion(turretDirection.x, turretDirection.y, turretDirection.z, turretDirection.w);
    this.projectileSystem.fireProjectile(tPos, tDir, undefined, weapon);
  }

  updateSelfTank(selfTank: any | null): void {
    this.selfTank = selfTank;
  }

  dealDamage(data: { id: string; damage: number; userId: string }): void {
    const attackerId = data.id;
    const damage = data.damage;
    const targetId = data.userId;
    if (this.myUserId === targetId) {
      if (this.selfTank) {
        this.selfTank.receiveDamage(damage, attackerId);
      }
    } else {
      const target = this.players[targetId] as Player;
      if (target && typeof target.receiveDamage === 'function') {
        target.receiveDamage(damage, attackerId);
      }
    }
  }

  playerDead(data: { id: string }): void {
    const playerId = data.id;
    const player = this.players[playerId] as Player;
    if (player && player.tankModel) {
      const position = player.tankModel.position.clone();
      player.death();
      const veh = this.projectileSystem.visualEffectHelper;
      if (veh && veh.createExplosion) {
        veh.createExplosion(position, 10);
      }
    }
    
    // Ensure we remove the reference so respawn can re-add the player
    if (this.players[playerId]) {
      delete this.players[playerId];
      console.log(`Cleared player mapping after death: ${playerId}`);
    }
  }

  addPlayer(data: PlayerData): void {
    const { id, name, color } = data;
    console.log(`Received addPlayer event for ${name} (${id}) with color ${color}`);
    const myId = socketService.getSocketId();
    if (id === myId) {
      console.log('Ignoring addPlayer for self');
      return;
    }
    if (this.players[id]) {
      console.log(`Player with ID ${id} already exists.`);
      return;
    }
    console.log('Adding Player', id);
    const player = new Player(this.scene, name, id, color, this.collidables);
    if (player.tankModel) this.collidables.push(player.tankModel);
    this.players[id] = player;
    console.log(`Successfully added player ${name} (${id}). Total players:`, Object.keys(this.players));
  }

  playerUpdate(data: { id: string } & ServerPlayerUpdate): void {
    const player = this.players[data.id];
    if (!player) {
      console.warn(`Player with ID ${data.id} is not added`);
      return;
    }
    player.updateFromServer(data as ServerPlayerUpdate);
  }

  removePlayer(playerId: string): void {
    const playerTank = this.players[playerId];
    if (playerTank) {
      playerTank.remove();
      delete this.players[playerId];
      console.log(`Removed player with ID ${playerId}`);
    }
  }

  // Initial spawn is server-driven; no client-side repositioning
}
